// src/components/ThemeSettingsDialog.tsx - ADMIN-ONLY VERSION
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Switch,
  FormControlLabel,
  TextField,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  CheckCircle as CheckIcon,
  Refresh as ResetIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useThemeContext, themePresets, ThemeColors } from '../contexts/ThemeContext';

interface ThemeSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const ThemeSettingsDialog: React.FC<ThemeSettingsDialogProps> = ({ open, onClose }) => {
  const {
    mode,
    currentPreset,
    customColors,
    toggleColorMode,
    setThemePreset,
    setCustomColors,
    resetToDefault,
    canEditTheme,
  } = useThemeContext();

  const [showCustom, setShowCustom] = useState(false);
  const [tempCustomColors, setTempCustomColors] = useState<ThemeColors>({
    primary: customColors?.primary || '#0D47A1',
    secondary: customColors?.secondary || '#FFC107',
    success: customColors?.success || '#4CAF50',
    warning: customColors?.warning || '#FF9800',
    error: customColors?.error || '#F44336',
    info: customColors?.info || '#2196F3',
  });

  const handlePresetSelect = async (presetId: string) => {
    if (!canEditTheme) return;
    await setThemePreset(presetId);
    setShowCustom(false);
  };

  const handleApplyCustom = async () => {
    if (!canEditTheme) return;
    await setCustomColors(tempCustomColors);
    setShowCustom(false);
  };

  const handleReset = async () => {
    if (!canEditTheme) return;
    await resetToDefault();
    setShowCustom(false);
    onClose();
  };

  const ColorPicker = ({ label, value, onChange, disabled }: { 
    label: string; 
    value: string; 
    onChange: (color: string) => void;
    disabled?: boolean;
  }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{
            width: 60,
            height: 40,
            border: '2px solid #ddd',
            borderRadius: 8,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        />
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          disabled={disabled}
          sx={{ flex: 1 }}
          placeholder="#000000"
        />
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon color="primary" />
          <Typography variant="h6">Company Theme Settings</Typography>
          {canEditTheme && (
            <Chip 
              icon={<BusinessIcon />} 
              label="Admin" 
              size="small" 
              color="primary" 
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Admin Permission Alert */}
        {!canEditTheme && (
          <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600}>
              Administrator Access Required
            </Typography>
            <Typography variant="body2">
              Only company administrators can modify theme settings. Changes apply to all users in your organization.
            </Typography>
          </Alert>
        )}

        {/* Company-wide Notice */}
        {canEditTheme && (
          <Alert severity="info" icon={<BusinessIcon />} sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600}>
              Company-Wide Settings
            </Typography>
            <Typography variant="body2">
              Theme changes will apply to all users in your organization immediately.
            </Typography>
          </Alert>
        )}

        {/* Dark Mode Toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Appearance Mode
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === 'dark'}
                    onChange={toggleColorMode}
                    icon={<LightModeIcon />}
                    checkedIcon={<DarkModeIcon />}
                    disabled={!canEditTheme}
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1" fontWeight={500}>
                      {mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {canEditTheme 
                        ? 'Switch between light and dark themes for all users' 
                        : 'Contact your administrator to change this setting'}
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Theme Presets */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Color Themes
            </Typography>
            {canEditTheme && (
              <Chip
                label="Custom"
                color={customColors ? 'primary' : 'default'}
                onClick={() => setShowCustom(!showCustom)}
                icon={<PaletteIcon />}
                variant={showCustom ? 'filled' : 'outlined'}
              />
            )}
          </Box>

          {!showCustom && (
            <Grid container spacing={2}>
              {themePresets.map((preset) => {
                const isActive = !customColors && currentPreset.id === preset.id;
                return (
                  <Grid item xs={12} sm={6} md={4} key={preset.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: isActive ? 2 : 1,
                        borderColor: isActive ? 'primary.main' : 'divider',
                        position: 'relative',
                        opacity: canEditTheme ? 1 : 0.7,
                      }}
                    >
                      <CardActionArea 
                        onClick={() => handlePresetSelect(preset.id)}
                        disabled={!canEditTheme}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {preset.name}
                            </Typography>
                            {isActive && <CheckIcon color="primary" fontSize="small" />}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: preset.colors.primary,
                                border: '2px solid white',
                                boxShadow: 1,
                              }}
                            />
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: preset.colors.secondary,
                                border: '2px solid white',
                                boxShadow: 1,
                              }}
                            />
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: preset.colors.success,
                                border: '2px solid white',
                                boxShadow: 1,
                              }}
                            />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Custom Color Picker */}
          {showCustom && canEditTheme && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a custom color scheme for your organization
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Primary Color"
                      value={tempCustomColors.primary}
                      onChange={(color) => setTempCustomColors({ ...tempCustomColors, primary: color })}
                    />
                    <ColorPicker
                      label="Secondary Color"
                      value={tempCustomColors.secondary}
                      onChange={(color) => setTempCustomColors({ ...tempCustomColors, secondary: color })}
                    />
                    <ColorPicker
                      label="Success Color"
                      value={tempCustomColors.success || '#4CAF50'}
                      onChange={(color) => setTempCustomColors({ ...tempCustomColors, success: color })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Warning Color"
                      value={tempCustomColors.warning || '#FF9800'}
                      onChange={(color) => setTempCustomColors({ ...tempCustomColors, warning: color })}
                    />
                    <ColorPicker
                      label="Error Color"
                      value={tempCustomColors.error || '#F44336'}
                      onChange={(color) => setTempCustomColors({ ...tempCustomColors, error: color })}
                    />
                    <ColorPicker
                      label="Info Color"
                      value={tempCustomColors.info || '#2196F3'}
                      onChange={(color) => setTempCustomColors({ ...tempCustomColors, info: color })}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyCustom}
                    fullWidth
                  >
                    Apply Custom Colors
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setShowCustom(false)}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Preview Section */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Preview
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="Primary" color="primary" />
            <Chip label="Secondary" color="secondary" />
            <Chip label="Success" color="success" />
            <Chip label="Warning" color="warning" />
            <Chip label="Error" color="error" />
            <Chip label="Info" color="info" />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small">Contained</Button>
            <Button variant="outlined" size="small">Outlined</Button>
            <Button variant="text" size="small">Text</Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {canEditTheme && (
          <Tooltip title="Reset to default theme">
            <Button
              startIcon={<ResetIcon />}
              onClick={handleReset}
              color="error"
              variant="outlined"
            >
              Reset to Default
            </Button>
          </Tooltip>
        )}
        <Button onClick={onClose} variant="contained">
          {canEditTheme ? 'Save & Close' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThemeSettingsDialog;